import React, { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { api, type GroupTalkRecommendationMemberDto, type GroupTalkRecommendationResponse } from "../lib/api";
import { logError, toUserMessage } from "../lib/errors";
import {
  buildParticipantsFromTemplate,
  createParticipant,
  groupTalkTemplates,
  normalizeGroupTalkMemberId,
  normalizeGroupTalkStanceId,
  type GroupTalkParticipant,
  type GroupTalkRoleStanceOption,
  type GroupTalkTemplateId,
} from "../lib/groupTalk";
import {
  loadGroupTalkDraft,
  saveGroupTalkDraft,
  type GroupTalkDraft,
} from "../lib/groupTalkSessionStorage";

type GroupTalkHubRouteState = {
  errorMessage?: string;
  draft?: GroupTalkDraft;
};

const templateOrder: GroupTalkTemplateId[] = ["management", "project", "professional", "custom"];
const suggestionExamples = [
  "主力商品の売上が上がらない",
  "新機能の優先順位を相談したい",
  "契約書のリスクを見たい",
  "価格を見直したい",
];

function isKnownTemplateId(value: string | null | undefined): value is GroupTalkTemplateId {
  return value === "management" || value === "project" || value === "professional" || value === "custom";
}

function findTemplate(templateId: GroupTalkTemplateId) {
  return groupTalkTemplates.find((template) => template.id === templateId) ?? groupTalkTemplates[0];
}

function normalizeRecommendationStances(member: GroupTalkRecommendationMemberDto): GroupTalkRoleStanceOption[] {
  const stances = Array.isArray(member.available_stances) ? member.available_stances : [];
  return stances
    .map((stance) => {
      const stanceId = normalizeGroupTalkStanceId(stance.stance);
      if (!stanceId || typeof stance.label !== "string" || stance.label.trim().length === 0) return null;
      return {
        id: stanceId,
        label: stance.label.trim(),
        description: typeof stance.description === "string" ? stance.description.trim() : "",
      };
    })
    .filter((stance): stance is GroupTalkRoleStanceOption => stance !== null)
    .slice(0, 3);
}

function buildRecommendationParticipants(response: GroupTalkRecommendationResponse): GroupTalkParticipant[] {
  const members = Array.isArray(response.recommended_members) ? response.recommended_members : [];
  const participants = members
    .map((member) => {
      const memberId = normalizeGroupTalkMemberId(
        member.member_id ?? (member.role === "facilitator" ? "mei" : undefined)
      );
      return createParticipant(memberId, normalizeGroupTalkStanceId(member.default_stance), {
        fixed: true,
        availableStances: normalizeRecommendationStances(member),
        displayRole: typeof member.display_role === "string" ? member.display_role.trim() : undefined,
        imageVariant: member.image_variant === 2 ? 2 : 1,
      });
    })
    .filter((participant, index, items) => {
      if (participant.memberId === "mei") return index === items.findIndex((item) => item.memberId === "mei");
      return index === items.findIndex((item) => item.memberId === participant.memberId);
    });

  if (participants.some((participant) => participant.memberId === "mei")) return participants;
  return [createParticipant("mei", null, { fixed: true, imageVariant: 1 }), ...participants];
}

function buildRecommendationDraft(response: GroupTalkRecommendationResponse, consultationText: string): GroupTalkDraft {
  const templateId = isKnownTemplateId(response.recommended_template) && response.recommended_template !== "custom"
    ? response.recommended_template
    : "management";
  const fallbackTemplate = findTemplate(templateId);
  const participants = buildRecommendationParticipants(response);
  const safeParticipants = participants.length > 1 ? participants : buildParticipantsFromTemplate(templateId);

  return {
    templateId,
    title:
      typeof response.template_label === "string" && response.template_label.trim().length > 0
        ? response.template_label.trim()
        : fallbackTemplate.title,
    description:
      typeof response.template_description === "string" && response.template_description.trim().length > 0
        ? response.template_description.trim()
        : fallbackTemplate.description,
    participants: safeParticipants,
    mode: "recommend",
    consultationText,
    recommendationReason: typeof response.reason === "string" ? response.reason.trim() : "",
    recommendationConfidence: typeof response.confidence === "string" ? response.confidence.trim() : "",
    memberEditingLocked: true,
  };
}

function buildTemplateDraft(templateId: GroupTalkTemplateId, consultationText: string): GroupTalkDraft {
  const template = findTemplate(templateId);
  const mode = templateId === "custom" ? "custom" : "template";

  return {
    templateId,
    title: template.title,
    description: template.description,
    participants: buildParticipantsFromTemplate(templateId).map((participant) => ({
      ...participant,
      fixed: participant.memberId === "mei",
      imageVariant: mode === "custom" ? 2 : 1,
    })),
    mode,
    consultationText,
    memberEditingLocked: false,
  };
}

export default function GroupTalkHub() {
  const navigate = useNavigate();
  const location = useLocation();
  const routeState = (typeof location.state === "object" && location.state ? location.state : {}) as GroupTalkHubRouteState;
  const restoredDraft = useMemo(() => routeState.draft ?? loadGroupTalkDraft(), [routeState.draft]);
  const [consultationText, setConsultationText] = useState(restoredDraft?.consultationText ?? "");
  const [routeError, setRouteError] = useState(routeState.errorMessage ?? "");
  const [recommendError, setRecommendError] = useState("");
  const [recommending, setRecommending] = useState(false);

  const orderedTemplates = useMemo(
    () => templateOrder.map((id) => findTemplate(id)),
    []
  );

  const openPreview = (draft: GroupTalkDraft) => {
    saveGroupTalkDraft(draft);
    navigate("/talk/group/preview", {
      state: { draft },
    });
  };

  const handleRecommend = async () => {
    const trimmed = consultationText.trim();
    if (!trimmed || recommending) return;

    setRecommending(true);
    setRecommendError("");
    setRouteError("");

    try {
      const response = await api.recommendGroupTalk(trimmed);
      openPreview(buildRecommendationDraft(response, trimmed));
    } catch (errorValue) {
      logError(errorValue);
      setRecommendError(`${toUserMessage(errorValue)} テンプレートから選んで進めることもできます。`);
    } finally {
      setRecommending(false);
    }
  };

  const handleTemplateSelect = (templateId: GroupTalkTemplateId) => {
    setRouteError("");
    setRecommendError("");
    openPreview(buildTemplateDraft(templateId, consultationText.trim()));
  };

  return (
    <main className="min-h-screen overflow-x-hidden bg-[#FDF8F5] px-4 py-6 text-[#4A3E39] sm:px-6 sm:py-10">
      <div className="mx-auto w-full max-w-5xl">
        <header className="mb-6 flex items-center justify-between">
          <Link to="/talk" className="text-sm font-bold text-[#A67C52] hover:text-[#7C5A4E]">
            ←戻る
          </Link>
          <p className="text-sm font-bold tracking-tight text-[#7C5A4E]">AI社長室</p>
          <Link to="/home" className="text-xs font-bold text-[#9C867D] hover:text-[#7C5A4E]">
            WizMate
          </Link>
        </header>

        <section className="border-b border-[#EADBC8] pb-6">
          <p className="text-xs font-black uppercase tracking-[0.18em] text-[#A67C52]">AI壁打ちルーム</p>
          <h1 className="mt-3 text-3xl font-black tracking-tight text-[#5D4D46] sm:text-4xl">
            相談したいテーマを選ぶ
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-7 text-[#6B5851]">
            AIメンバーと一緒に、悩みを整理して次の一手を考えます。
          </p>
        </section>

        {routeError ? (
          <p role="alert" className="mt-5 rounded-[1rem] border border-[#F0C8BA] bg-[#FFF7F1] px-4 py-3 text-sm font-bold text-[#C06D5A]">
            {routeError}
          </p>
        ) : null}

        <div className="mt-6 space-y-5">
          <section className="rounded-[1.7rem] border border-[#E2CDBF] bg-white px-5 py-6 shadow-[0_18px_44px_rgba(124,90,78,0.10)] sm:px-6">
            <h2 className="text-lg font-black text-[#5D4D46]">相談内容からおすすめ</h2>
            <p className="mt-2 text-sm leading-6 text-[#6B5851]">
              悩みをそのまま入力すると、相談内容に合わせておすすめのテーマとメンバーを提案します。
            </p>
            <textarea
              value={consultationText}
              onChange={(event) => setConsultationText(event.currentTarget.value)}
              placeholder="例：主力商品の売上が上がらない"
              rows={4}
              disabled={recommending}
              className="mt-4 min-h-[132px] w-full resize-none rounded-[1.2rem] border border-[#EADBC8] bg-[#FFFDFB] px-4 py-3 text-sm leading-6 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#D1907B]/20 disabled:opacity-70"
            />
            <div className="mt-3 flex flex-wrap gap-2">
              {suggestionExamples.map((example) => (
                <button
                  key={example}
                  type="button"
                  onClick={() => setConsultationText(example)}
                  disabled={recommending}
                  className="rounded-full border border-[#EADBC8] bg-[#FFFDFB] px-3 py-1.5 text-xs font-bold text-[#7C5A4E] transition hover:bg-[#FFF7F1] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {example}
                </button>
              ))}
            </div>
            {recommendError ? (
              <p role="alert" className="mt-3 text-sm font-bold text-[#C06D5A]">
                {recommendError}
              </p>
            ) : null}
            <button
              type="button"
              onClick={() => void handleRecommend()}
              disabled={recommending || !consultationText.trim()}
              className="mt-5 min-h-[46px] w-full rounded-full bg-[#5D4D46] px-5 py-2 text-sm font-black text-white transition hover:bg-[#4A3E39] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              {recommending ? "おすすめを確認中..." : "おすすめメンバーを確認"}
            </button>
          </section>

          <section className="rounded-[1.3rem] border border-[#EADBC8] bg-white px-5 py-4 shadow-sm">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-base font-black text-[#5D4D46]">続きから始める</h2>
                <p className="mt-1 text-sm leading-6 text-[#6B5851]">以前の相談を開きます。</p>
              </div>
              <Link
                to="/talk/group/sessions"
                className="inline-flex min-h-[40px] shrink-0 items-center justify-center rounded-full border border-[#D9C5B9] bg-[#FFFDFB] px-5 py-2 text-sm font-black text-[#6B5851] transition hover:bg-[#FFF7F1]"
              >
                進む →
              </Link>
            </div>
          </section>
        </div>

        <section className="mt-6">
          <h2 className="text-lg font-black text-[#5D4D46]">テンプレートから選ぶ</h2>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {orderedTemplates.map((template) => (
              <button
                key={template.id}
                type="button"
                onClick={() => handleTemplateSelect(template.id)}
                className="min-h-[132px] rounded-[1.4rem] border border-[#EADBC8] bg-white px-5 py-4 text-left shadow-sm transition hover:border-[#CFAE9B] hover:bg-[#FFFDFB]"
              >
                <span className="block text-base font-black text-[#5D4D46]">{template.title}</span>
                <span className="mt-2 block text-sm leading-6 text-[#6B5851]">{template.description}</span>
              </button>
            ))}
          </div>
        </section>
      </div>
    </main>
  );
}
